// File: src/contexts/LanguageContext.js

import React, { createContext, useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Import configurations
import {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  isLocalizablePath,
  getLocaleFromPath,
  addLocaleToPath,
  getDefaultLocaleForLanguage
} from '../config/i18n.config';

// Import local translations for fallback and local development
import deTranslations from '../locales/de.json';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';
import frTranslations from '../locales/fr.json';
import nlTranslations from '../locales/nl.json';
import trTranslations from '../locales/tr.json';

import deCapabilities from '../locales/capabilities_de.json';
import enCapabilities from '../locales/capabilities_en.json';
import esCapabilities from '../locales/capabilities_es.json';
import frCapabilities from '../locales/capabilities_fr.json';
import nlCapabilities from '../locales/capabilities_nl.json';
import trCapabilities from '../locales/capabilities_tr.json';

// Local translations object (used for local development and fallback)
const localTranslations = {
  'de-de': { ...deTranslations, capabilities: deCapabilities },
  'en-ca': { ...enTranslations, capabilities: enCapabilities },
  'en-gb': { ...enTranslations, capabilities: enCapabilities },
  'en-us': { ...enTranslations, capabilities: enCapabilities },
  'es-es': { ...esTranslations, capabilities: esCapabilities },
  'es-mx': { ...esTranslations, capabilities: esCapabilities },
  'fr-ca': { ...frTranslations, capabilities: frCapabilities },
  'fr-fr': { ...frTranslations, capabilities: frCapabilities },
  'nl-nl': { ...nlTranslations, capabilities: nlCapabilities },
  'tr-tr': { ...trTranslations, capabilities: trCapabilities }
};

// Initialize translations with local files
let translations = { ...localTranslations };

// S3 language file URLs
const S3_LANGUAGE_BASE_URL = 'https://allegory-public.s3.ca-central-1.amazonaws.com/language/';

// Map locale codes to S3 file language codes
const LOCALE_TO_S3_LANGUAGE = {
  'de-de': 'de',
  'en-ca': 'en',
  'en-gb': 'en',
  'en-us': 'en',
  'es-es': 'es',
  'es-mx': 'es',
  'fr-ca': 'fr',
  'fr-fr': 'fr',
  'nl-nl': 'nl',
  'tr-tr': 'tr'
};

// Function to determine if we should use S3 files
const shouldUseS3Files = () => {
  const appEnv = process.env.REACT_APP_APP_ENV;
  return appEnv === 'dev' || appEnv === 'staging' || appEnv === 'prod';
};

// Function to fetch language files from S3
const fetchLanguageFromS3 = async (languageCode) => {
  try {
    const response = await fetch(`${S3_LANGUAGE_BASE_URL}${languageCode}.json`);
    if (!response.ok) {
      throw new Error(`Failed to fetch language file: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${languageCode} language file:`, error);
    return null;
  }
};

const LanguageContext = createContext();

const getBrowserLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  
  const browserLang = navigator.language || navigator.userLanguage;
  const shortLang = browserLang.split('-')[0];
  
  // Check if the exact browser language is supported
  if (SUPPORTED_LOCALES[browserLang]) {
    return browserLang;
  }
  
  // Get the default locale for this language
  const defaultForLanguage = getDefaultLocaleForLanguage(shortLang);
  if (defaultForLanguage) {
    return defaultForLanguage;
  }
  
  return DEFAULT_LOCALE;
};

// Keep your existing interpolateString function
const interpolateString = (str, params) => {
  if (!params || typeof str !== 'string') return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return params[key] !== undefined ? params[key] : `{{${key}}}`;
  });
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize language with a more resilient approach
  const [language, setLanguage] = useState(() => {
    // If we have router context, check URL
    if (location) {
      const pathLocale = getLocaleFromPath(location.pathname);
      if (pathLocale) return pathLocale;
    }
    
    // Check localStorage
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && localTranslations[savedLanguage]) return savedLanguage;
    
    // Use browser language
    return getBrowserLanguage();
  });
  
  const [currentTranslations, setCurrentTranslations] = useState(translations[language]);
  const [isLoading, setIsLoading] = useState(false);

  // Effect to load S3 language files if needed
  useEffect(() => {
    const loadS3LanguageFiles = async () => {
      // Only load from S3 in non-local environments
      if (!shouldUseS3Files()) {
        return;
      }

      setIsLoading(true);
      
      // Get unique S3 language codes to fetch
      const uniqueS3Languages = [...new Set(Object.values(LOCALE_TO_S3_LANGUAGE))];
      
      // Fetch all language files in parallel
      const fetchPromises = uniqueS3Languages.map(async (langCode) => {
        const data = await fetchLanguageFromS3(langCode);
        return { langCode, data };
      });
      
      // Wait for all fetches to complete
      const results = await Promise.all(fetchPromises);
      
      // Process results
      const s3Translations = {};
      
      results.forEach(({ langCode, data }) => {
        if (data) {
          // For each locale that maps to this S3 language code
          Object.entries(LOCALE_TO_S3_LANGUAGE).forEach(([locale, s3Lang]) => {
            if (s3Lang === langCode) {
              // Create a new translation object with the S3 data and local capabilities
              s3Translations[locale] = {
                ...data,
                capabilities: localTranslations[locale].capabilities
              };
            }
          });
        }
      });
      
      // If we have any successful S3 translations, update our translations object
      if (Object.keys(s3Translations).length > 0) {
        translations = {
          ...localTranslations,  // Keep local as fallback
          ...s3Translations      // Override with S3 data
        };
        
        // Update current translations if language is already set
        setCurrentTranslations(translations[language]);
      }
      
      setIsLoading(false);
    };
    
    loadS3LanguageFiles();
  }, []);  // Empty dependency array means this runs once on mount

  // Effect for URL and language synchronization
  useEffect(() => {
    if (!location || !navigate) return;
    
    const currentLocale = getLocaleFromPath(location.pathname);
    
    if (isLocalizablePath(location.pathname) && !currentLocale) {
      const newPath = addLocaleToPath(location.pathname, language);
      navigate(newPath, { replace: true });
    }
    else if (currentLocale && currentLocale !== language) {
      setLanguage(currentLocale);
    }
  }, [location, language, navigate]);

  useEffect(() => {
    setCurrentTranslations(translations[language]);
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
      document.documentElement.lang = SUPPORTED_LOCALES[language].languageCode;
    }
  }, [language]);

  const changeLanguage = (newLanguage) => {
    if (!translations[newLanguage]) {
      console.warn(`Language ${newLanguage} is not supported.`);
      return;
    }

    if (location && navigate && isLocalizablePath(location.pathname)) {
      const newPath = addLocaleToPath(location.pathname, newLanguage);
      navigate(newPath);
    }
    
    setLanguage(newLanguage);
  };

  const translate = (key, paramsOrCategory = null, category = 'default') => {
    try {
      let translatedText;
      let params = null;

      if (typeof paramsOrCategory === 'string') {
        category = paramsOrCategory;
      } else {
        params = paramsOrCategory;
      }
      
      if (category === 'capabilities') {
        const [section, itemKey] = key.split('.');
        translatedText = currentTranslations?.capabilities?.[section]?.[itemKey] || key;
      } else {
        translatedText = currentTranslations?.[key] || key;
      }

      return params ? interpolateString(translatedText, params) : translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      return key;
    }
  };

  const value = {
    language,
    changeLanguage,
    t: translate,
    supportedLanguages: Object.keys(translations),
    currentLanguage: language,
    isLocalizablePath,
    getLocaleFromPath,
    addLocaleToPath,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;